import { useChat } from "@ai-sdk/vue";
import { generateId, lastAssistantMessageIsCompleteWithApprovalResponses } from "ai";
import { textOf } from "~/utils/helpers/copilot/message-parts";
import { useCopilotTransport } from "~/composables/copilot/useCopilotTransport";
import { bindChatToAuthSession } from "~/utils/helpers/auth/chat-session";
import { copilotStreamConsumer, useCopilotStream } from "~/composables/copilot/useCopilotStream";
import { useTrainingPrefillImport } from "~/composables/copilot/useTrainingPrefillImport";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import { useAuthStore } from "~/store/auth/auth.store";
import { createCopilotHistory } from "~/utils/helpers/copilot/history";
import { useCopilotFeedbackStore } from "~/store/copilot/copilot-feedback.store";
import { CopilotChatStatusEnum, CopilotDocTypeEnum } from "~/utils/enums/copilot/chat.enums";
import { CopilotErrorCodeEnum, CopilotErrorRecoveryEnum } from "~/utils/enums/copilot/errors.enums";
import { AttendanceImportOutcomeEnum } from "~/utils/enums/attendance-review.enums";
import { attendanceImportOutcomeOf } from "~/utils/helpers/attendance/sheet-import";
import {
  buildProgramUrlPrompt,
  copilotFileErrorOf,
  createCopilotRetryTracker,
  originatingUserMessageOf,
  programUrlFromPrompt,
  transportErrorOf,
} from "~/utils/helpers/copilot/errors";
import { attendanceRunsOf } from "~/utils/helpers/attendance/run-envelope";
import { copilotDocTypePromptOf, copilotSendTextOf } from "~/utils/helpers/copilot/send-intent";
import type { ICopilotError } from "~/utils/interfaces/copilot-blocks-interfaces";
import type {
  ICopilotAction,
  ICopilotApproval,
  ICopilotChatEffects,
  ICopilotPendingAttachment,
  ICopilotUIMessage,
} from "~/utils/interfaces/copilot-interfaces";

/**
 * Everything the Copilote conversation does: the SSE round-trip, the file the admin staged, the
 * program URL, the error recoveries and their retry budget, and the reset that starts a new thread.
 *
 * Split out of `CopilotPanel` so the panel is layout only. Whatever needs the panel's own DOM — the
 * caret in the URL field or in the composer, reopening the file picker — arrives as `effects`
 * rather than as template refs.
 */
export const useCopilotChat = (effects: ICopilotChatEffects) => {
  const attendanceReviewStore = useAttendanceReviewStore();
  const copilotFeedbackStore = useCopilotFeedbackStore();
  const retryTracker = createCopilotRetryTracker();
  const history = createCopilotHistory();
  const authStore = useAuthStore();
  const historyScope = computed(() => {
    const userId = authStore.user?.userId;
    const companyId = authStore.user?.mainEntityId?.companyId;
    const jwt = authStore.jwtPayload;
    if (!userId || !companyId || jwt?.sub !== userId || jwt.companyId !== companyId) return null;
    return JSON.stringify([companyId, userId, jwt.logAsAdminSub ?? null]);
  });

  /** Memory thread id of the assistant — regenerated on reset so a new chat starts blank. */
  const chatId = ref(generateId());

  const { messages, status, error, sendMessage, regenerate, stop, clearError, addToolApprovalResponse } =
    useChat<ICopilotUIMessage>({
      transport: useCopilotTransport(chatId),
      // Answering an approval hands the conversation back to the assistant, which runs the action.
      sendAutomaticallyWhen: lastAssistantMessageIsCompleteWithApprovalResponses,
    });
  const { openReview, setSourceUrl, reset: resetImport } = useTrainingPrefillImport();

  /*
   * Neither `declareAttendance` nor `reviewTraining` opens a screen by itself: both end their answer
   * on a card, and the admin decides when to leave the conversation — for a sheet, after checking
   * the session it was matched to, which is the only place that session is ever named.
   * `messages` is a shallow ref the SDK re-triggers on every chunk (tool parts are updated in
   * place), so a plain watcher sees each state change — no `deep` needed.
   */
  const stream = useCopilotStream([
    copilotStreamConsumer("attendanceRun", attendanceRunsOf, (run) =>
      attendanceReviewStore.openRun(run, { openReviewScreen: false }),
    ),
    copilotStreamConsumer(
      "attendanceBlock",
      (messages) =>
        messages.flatMap((message) => {
          const outcome = attendanceImportOutcomeOf([message]);
          return outcome.kind === AttendanceImportOutcomeEnum.ERROR &&
            outcome.error.code === CopilotErrorCodeEnum.MULTI_TIMESLOT_SESSION
            ? [{ key: message.id, payload: outcome.error }]
            : [];
        }),
      () => attendanceReviewStore.reset(),
    ),
  ]);

  watch(messages, stream.consume);

  const draft = ref("");
  const pendingAttachment = ref<ICopilotPendingAttachment | null>(null);
  /** Last prompt this composable wrote in the composer — anything else in `draft` the admin typed. */
  const prefilledPrompt = ref("");
  const programUrl = ref("");
  const localError = ref<ICopilotError | null>(null);
  const isCorrectingUrl = ref(false);

  const isBusy = computed(
    () => status.value === CopilotChatStatusEnum.SUBMITTED || status.value === CopilotChatStatusEnum.STREAMING,
  );

  const transportError = computed(() => (error.value ? transportErrorOf(error.value) : null));
  const transportOriginatingMessage = computed(() =>
    originatingUserMessageOf(messages.value, messages.value.length - 1),
  );
  const canRetryTransportError = computed(
    () => !!transportOriginatingMessage.value && retryTracker.canRetry(transportOriginatingMessage.value.id),
  );

  function clearDraft() {
    draft.value = "";
    prefilledPrompt.value = "";
  }

  /**
   * Shows a staged file's prompt in the composer, so what will be sent is visible and editable. The
   * admin's own words are never overwritten — a prompt written here is, on a swap or a removal.
   */
  function setPromptDraft(prompt: string) {
    if (draft.value.trim() && draft.value !== prefilledPrompt.value) return;

    draft.value = prompt;
    prefilledPrompt.value = prompt;
  }

  function originatingMessageAt(index: number): ICopilotUIMessage | null {
    return originatingUserMessageOf(messages.value, index);
  }

  function canRetryAt(index: number): boolean {
    const originatingMessage = originatingMessageAt(index);

    return !!originatingMessage && retryTracker.canRetry(originatingMessage.id);
  }

  function onSend() {
    if (isBusy.value) return;

    const attachment = pendingAttachment.value;
    const typed = draft.value.trim();
    if (!attachment && !typed) return;

    if (attachment) {
      /*
       * The assistant answers a sheet with the `declareAttendance` tool, whose output opens the
       * verification screen — but that screen also shows the sheet, and the tool output does not carry
       * it. Keeping the sent file aside is what gives the viewer something to render. Stashed for every
       * PDF: a program left here is never rendered, the attendance review is the only thing reading it.
       */
      attendanceReviewStore.setPendingDocument(attachment.file);

      const files = new DataTransfer();
      files.items.add(attachment.file);

      setSourceUrl(null);
      sendMessage({ text: copilotSendTextOf(typed, attachment.file.name, attachment.docType), files: files.files });
      pendingAttachment.value = null;
      clearDraft();

      return;
    }

    sendMessage({ text: typed });
    clearDraft();
  }

  async function onErrorRecovery(
    recovery: CopilotErrorRecoveryEnum,
    errorMessageId: string | null,
    originatingMessage: ICopilotUIMessage | null,
  ) {
    if (recovery === CopilotErrorRecoveryEnum.RETRY) {
      if (isBusy.value || !originatingMessage || !retryTracker.recordRetry(originatingMessage.id)) return;

      clearError();
      await regenerate({ messageId: errorMessageId ?? originatingMessage.id });

      return;
    }

    if (recovery === CopilotErrorRecoveryEnum.CORRECT_INPUT) {
      if (!originatingMessage) return;

      const submittedUrl = programUrlFromPrompt(textOf(originatingMessage));
      if (!submittedUrl) return;

      programUrl.value = submittedUrl;
      isCorrectingUrl.value = true;
      await nextTick();
      effects.focusUrlInput();

      return;
    }

    if (recovery === CopilotErrorRecoveryEnum.ATTACH_PDF) effects.openProgramPicker();
  }

  /**
   * Single choke point for every input path — the drag-and-drop one bypasses `accept`. The file is
   * only *staged*: nothing is sent until the admin presses send, so they can add a message, swap the
   * file, or change their mind. `docType` is known when it comes through a drop-zone shortcut, and
   * its prompt lands in the composer right away instead of being derived at send time.
   */
  function onFile(file: File, docType?: CopilotDocTypeEnum) {
    const validationError = copilotFileErrorOf(file);
    if (validationError) {
      localError.value = validationError;

      return;
    }

    localError.value = null;
    isCorrectingUrl.value = false;
    pendingAttachment.value = { file, docType };
    setPromptDraft(copilotDocTypePromptOf(file.name, docType));
    // Once the prompt is rendered, the caret sits at its end: the admin can complete it or send it.
    void nextTick(effects.focusComposer);
  }

  /** Dropping the file drops the prompt it put in the composer, unless the admin rewrote it. */
  function onRemoveAttachment() {
    pendingAttachment.value = null;
    setPromptDraft("");
  }

  function onAnalyzeUrl() {
    const url = programUrl.value.trim();
    if (!url) return;

    setSourceUrl(url);
    localError.value = null;
    isCorrectingUrl.value = false;
    clearError();
    sendMessage({ text: buildProgramUrlPrompt(url) });
    programUrl.value = "";
  }

  /** Retry of an answer that failed — the error block's affordance, and the tool card's own. */
  async function onRetryAt(errorMessageId: string, index: number) {
    await onErrorRecovery(CopilotErrorRecoveryEnum.RETRY, errorMessageId, originatingMessageAt(index));
  }

  function onPickAction(action: ICopilotAction) {
    if (isBusy.value) return;

    sendMessage({ text: action.prompt });
  }

  /** Approval-gated tools run server-side once the answer is sent back with the next round-trip. */
  function onApprove({ approvalId }: ICopilotApproval) {
    addToolApprovalResponse({ id: approvalId, approved: true });
  }

  function onDeny({ approvalId }: ICopilotApproval) {
    addToolApprovalResponse({ id: approvalId, approved: false });
  }

  function resetConversation() {
    history.clear();
    stop();
    // Fresh memory thread: keeping the id would make the "new" chat remember the cleared one.
    chatId.value = generateId();
    messages.value = [];
    clearDraft();
    pendingAttachment.value = null;
    programUrl.value = "";
    localError.value = null;
    isCorrectingUrl.value = false;
    clearError();
    copilotFeedbackStore.reset();
    stream.reset();
    resetImport();
    attendanceReviewStore.reset();
    retryTracker.reset();
  }

  watch(
    historyScope,
    (scope, previous) => {
      if (previous && scope !== previous) resetConversation();
    },
    { flush: "sync" },
  );

  onMounted(() => {
    const snapshot = historyScope.value ? history.load(historyScope.value) : null;
    if (snapshot) {
      stream.prime(snapshot.messages);
      chatId.value = snapshot.chatId;
      messages.value = snapshot.messages;
    }
    watch([messages, status, chatId], () => {
      if (!historyScope.value || isBusy.value) return;
      if (!messages.value.length) history.clear();
      else history.save(historyScope.value, chatId.value, messages.value);
    });
  });

  const removeAuthSessionListener = bindChatToAuthSession(resetConversation);

  onUnmounted(() => {
    removeAuthSessionListener();
    void stop();
  });

  return {
    chatId,
    messages,
    error,
    draft,
    pendingAttachment,
    programUrl,
    localError,
    isCorrectingUrl,
    isBusy,
    transportError,
    transportOriginatingMessage,
    canRetryTransportError,
    originatingMessageAt,
    canRetryAt,
    openReview,
    onSend,
    onErrorRecovery,
    onRetryAt,
    onFile,
    onRemoveAttachment,
    onAnalyzeUrl,
    onPickAction,
    onApprove,
    onDeny,
    resetConversation,
    stop,
    clearError,
  };
};
